/**
 * OrdoAppShell — sidebar + topbar + content area for the
 * consolidated app. Every authenticated route renders inside this
 * shell except `/kiosk` (fullscreen POS) and `/profile` (the avatar-
 * menu surface), which can opt out via their own route configs.
 *
 * Plan 03 added the top bar (per mockup feedback #1): the topbar is
 * now an explicit `@ordo/ui/app-topbar` mount with the alerts button +
 * search trigger. The avatar menu lives in the sidebar's footer per
 * mockup feedback #5 ("Account belongs in the sidebar, fixed to
 * bottom-left").
 */

import { AppShell as AppShellRoot, AppShellContent, AppShellMain, useSidebarState } from '@ordo/ui/app-shell'
import { AppSidebarDrawer } from '@ordo/ui/app-sidebar'
import { InstallPrompt } from '@ordo/ui/install-prompt'
import { Toaster } from '@ordo/ui/sonner'
import { usePostHog } from '@posthog/react'
import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router'

import { AnnouncementBanners } from '@/components/banners'
import { ChefOnboardingDocumentsPrompt } from '@/components/chefs/onboarding-documents-prompt'
import { FirstLoginPasswordPrompt } from '@/components/first-login-password-prompt'
import { ImpersonationBanner } from '@/components/impersonation-banner'
import { OrdoSidebar } from '@/components/ordo-sidebar'
import { OrdoTopBar } from '@/components/ordo-topbar'
import { useSwUpdateToast } from '@/hooks/use-sw-update-toast'
import { useProfile } from '@/stores/profile'

function DrawerSidebar() {
  const sidebarState = useSidebarState()
  return <OrdoSidebar onClose={() => sidebarState?.setOpen(false)} />
}

export function AppShell() {
  const { pathname } = useLocation()
  // Triggers the bootstrap fetch when the user authenticates.
  const { profile, loading } = useProfile()
  // "Ordo was updated — Refresh" toast when a deploy lands under this
  // open tab (renders through the <Toaster /> below; /cafeteria + /kiosk
  // never mount this shell, so kiosks are structurally excluded).
  useSwUpdateToast()

  const posthog = usePostHog()
  useEffect(() => {
    if (loading) {
      return
    }
    if (profile?.user) {
      const { id, email, firstName, lastName } = profile.user
      const name = [firstName, lastName].filter(Boolean).join(' ')
      posthog.identify(id, {
        ...(email ? { email } : {}),
        ...(name ? { name } : {}),
      })
    } else {
      posthog.reset()
    }
  }, [profile?.user?.id, loading, posthog])

  return (
    // Outer column so the impersonation banner spans the full viewport width
    // above the sidebar + content row. `AppShellRoot` (normally `h-dvh`) is
    // overridden to fill the remaining height below the banner.
    <div className="flex h-dvh flex-col overflow-hidden">
      <ImpersonationBanner />
      <AnnouncementBanners />
      <AppShellRoot pathname={pathname} className="h-full min-h-0 flex-1 text-foreground print-screen-only">
        <OrdoSidebar />
        <AppSidebarDrawer>
          <DrawerSidebar />
        </AppSidebarDrawer>
        <AppShellContent>
          <OrdoTopBar />
          {/* `overflow-y-auto` makes every page scroll naturally inside the
              main column without each page having to opt back in. Pages that
              still manage their own nested scroll containers (e.g. orders/
              kitchen with a sticky tab bar) keep working — nested scroll just
              takes over inside an already-scrollable parent. */}
          <AppShellMain className="overflow-y-auto overscroll-contain">
            <Outlet />
          </AppShellMain>
        </AppShellContent>
        <InstallPrompt appName="Ordo" dismissedKey="ordo-install-prompt-dismissed" />
        <FirstLoginPasswordPrompt />
        <ChefOnboardingDocumentsPrompt />
        <Toaster richColors />
      </AppShellRoot>
    </div>
  )
}
